import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'

export default class DrawersController extends Controller {
  static targets = ['dialog']
  static values = {
    swipeThreshold: { type: Number, default: 100 }
  }

  get #isMobile () { return window.innerWidth < 640 }

  disconnect () {
    this.#removeDialogListeners()
  }

  show (e) {
    this.openedDialog = this.#getDialog(e)
    this.#addDialogListeners()
    this.openedDialog?.showModal()
  }

  close (e) {
    this.#removeDialogListeners()
    this.#getDialog(e)?.close()
  }

  #getDialog (e) {
    const id = e.currentTarget.dataset.id
    return this.dialogTargets.find(dialog => dialog.id === id)
  }

  #addDialogListeners () {
    if (!this.openedDialog) return
    this._boundBackdropClick = this.#closeOnBackdropClick.bind(this)
    this.openedDialog.addEventListener('click', this._boundBackdropClick)

    if (!this.#isMobile) {
      this._boundTouchStart = this.#onTouchStart.bind(this)
      this._boundTouchMove = this.#onTouchMove.bind(this)
      this._boundTouchEnd = this.#onTouchEnd.bind(this)
      this.openedDialog.addEventListener('touchstart', this._boundTouchStart, { passive: true })
      this.openedDialog.addEventListener('touchmove', this._boundTouchMove, { passive: false })
      this.openedDialog.addEventListener('touchend', this._boundTouchEnd, { passive: true })
    }
  }

  #removeDialogListeners () {
    if (!this.openedDialog) return
    if (this._boundBackdropClick) this.openedDialog.removeEventListener('click', this._boundBackdropClick)
    if (this._boundTouchStart) this.openedDialog.removeEventListener('touchstart', this._boundTouchStart)
    if (this._boundTouchMove) this.openedDialog.removeEventListener('touchmove', this._boundTouchMove)
    if (this._boundTouchEnd) this.openedDialog.removeEventListener('touchend', this._boundTouchEnd)
  }

  #closeOnBackdropClick (e) {
    if (e.target === this.openedDialog) {
      this.openedDialog.close()
    }
  }

  #onTouchStart (event) {
    this.touchStartX = event.touches[0].clientX
    this.touchStartY = event.touches[0].clientY
    this.swiping = null
  }

  #onTouchMove (event) {
    const currentX = event.touches[0].clientX
    const currentY = event.touches[0].clientY
    const diffX = currentX - this.touchStartX
    const diffY = currentY - this.touchStartY

    if (this.swiping === null) {
      if (Math.abs(diffX) < 10 && Math.abs(diffY) < 10) return
      this.swiping = Math.abs(diffX) > Math.abs(diffY)
      if (this.swiping) {
        this.openedDialog.style.transition = 'none'
      }
    }

    if (!this.swiping) return

    event.preventDefault()
    const offset = Math.max(0, diffX)
    this.openedDialog.style.transform = `translateX(${offset}px)`
  }

  #onTouchEnd (event) {
    if (!this.swiping) {
      this.swiping = null
      return
    }

    const touchEndX = event.changedTouches[0].clientX
    const diff = touchEndX - this.touchStartX

    this.openedDialog.style.transition = 'transform 0.2s ease-out'

    if (diff > this.swipeThresholdValue) {
      this.openedDialog.style.transform = 'translateX(100%)'
      this.openedDialog.addEventListener('transitionend', () => {
        this.openedDialog.close()
        this.openedDialog.style.transform = ''
        this.openedDialog.style.transition = ''
      }, { once: true })
    } else {
      this.openedDialog.style.transform = ''
      this.openedDialog.style.transition = ''
    }

    this.swiping = null
  }
}

stimulus.register('drawers', DrawersController)
